import { openSearchPanel } from "@codemirror/search";
import type { ConfigServerEvent, ConfigServerInput } from "idfweb-runtime-protocol";

import type { FileView } from "./editors.ts";
import { KconfigModel, type MenuEntry, type ServerReply } from "./kconfig-model.ts";
import type { RuntimeLink } from "./runtime.ts";
import { createSettingsForm, type SettingsForm } from "./settings-form.ts";

/**
 * The sdkconfig editor: an `sdkconfig` file opens in the settings form, drawn
 * from the configuration server the runtime worker runs for the project's
 * build directory, which the worker configures first if no build has. Each
 * change is saved to the file at once. The tab's text mode is the file itself,
 * with the find bar open.
 */
export function sdkconfigView(runtime: RuntimeLink): FileView {
    return {
        matches: isSdkconfig,
        byDefault: true,
        open: { title: "Open Interactive Editor", icon: "settings" },
        close: { title: "Open as Text", icon: "go-to-file" },
        create: (path, saved) => new SdkconfigEditor(runtime, path, saved).handle(),
        textShown: (view) => {
            openSearchPanel(view);
        },
    };
}

/** The open sdkconfig editors by path, for `revealSetting`. */
const openEditors = new Map<string, SdkconfigEditor>();

/**
 * Show the row of the symbol `name` in the open editor of `path`, by
 * searching the form for it; resolves to the row, or to why it cannot be shown.
 */
export function revealSetting(path: string, name: string): Promise<Element | string> {
    return openEditors.get(path)?.reveal(name) ?? Promise.resolve(`${path} is not open in the sdkconfig editor.`);
}

/** Whether a file is an sdkconfig by its name, using the default ESP-IDF sdkconfig filename. */
export function isSdkconfig(path: string | undefined): boolean {
    return path !== undefined && path.slice(path.lastIndexOf("/") + 1) === "sdkconfig";
}

const LOADING = "Loading the configuration…";
const CONFIGURING = "Configuring the project, which the configuration is read from…";

class SdkconfigEditor {
    readonly element = document.createElement("div");
    readonly #runtime: RuntimeLink;
    readonly #path: string;
    /** The menu tree the running server was started with, and its modification time. */
    #menus: { path: string; mtime: number } | undefined;
    #port: MessagePort | undefined;
    #form: SettingsForm | undefined;
    #model: KconfigModel | undefined;
    #disposed = false;
    readonly #unsubscribe: () => void;

    constructor(runtime: RuntimeLink, path: string, saved: Promise<void>) {
        this.#runtime = runtime;
        this.#path = path;
        this.element.className = "wb-sdkconfig";
        this.#message(LOADING);
        openEditors.set(path, this);
        // The server loads the file when it starts: the text's edits go first.
        void saved.catch(() => {}).then(() => this.#connect());
        // A configure can change the Kconfig tree (a new target, a new component):
        // start over from it. Ninja runs CMake again by itself when the tree's
        // inputs change, so a finished build is checked for a new menu tree too.
        this.#unsubscribe = runtime.on((message) => {
            if (message.type === "configured" || message.type === "project-root") this.#connect();
            else if (message.type === "built") void this.#reconnectIfChanged();
        });
    }

    handle(): { element: HTMLElement; dispose: () => void } {
        return { element: this.element, dispose: () => this.#dispose() };
    }

    #connect(): void {
        if (this.#disposed) return;
        this.#close();
        const channel = new MessageChannel();
        const port = channel.port1;
        this.#port = port;
        let menus: Promise<MenuEntry[]> | undefined;
        port.onmessage = (event: MessageEvent<ConfigServerEvent>) => {
            if (this.#port !== port) return;
            const data = event.data;
            if ("started" in data) {
                if (data.started.sdkconfig !== this.#path) {
                    this.#message(
                        `The project's build uses ${data.started.sdkconfig}. Open that file to edit the configuration here, or edit this one as text.`,
                    );
                    this.#close();
                    return;
                }
                void this.#runtime.fs.stat(data.started.menus).then(
                    (stat) => (this.#menus = { path: data.started.menus, mtime: stat.mtimeMs }),
                    () => {},
                );
                menus = this.#runtime.fs
                    .readFile(data.started.menus)
                    .then((bytes) => JSON.parse(new TextDecoder().decode(bytes)) as MenuEntry[]);
            } else if ("configuring" in data) {
                this.#message(CONFIGURING);
            } else if ("reply" in data) {
                const reply = data.reply as ServerReply;
                if (this.#model !== undefined) this.#model.update(reply);
                else if (menus !== undefined)
                    void menus.then(
                        (tree) => this.#show(port, tree, reply),
                        (e: unknown) => this.#message(`The menu tree could not be read: ${String(e)}`),
                    );
                if (reply.error !== undefined && this.#model !== undefined) this.#error(reply.error.join("\n"));
            } else if ("exited" in data) {
                if (this.#port === port) this.#port = undefined;
                if (this.#model === undefined) this.#message(data.failure ?? "The configuration server stopped.", true);
            }
        };
        this.#runtime.post({ type: "config-server", port: channel.port2 }, [channel.port2]);
    }

    #show(port: MessagePort, menus: MenuEntry[], status: ServerReply): void {
        if (this.#port !== port || this.#disposed) return;
        const send = (request: object) => port.postMessage({ request } satisfies ConfigServerInput);
        this.#model = new KconfigModel(menus, status, send);
        this.#form?.dispose();
        this.#form = createSettingsForm(this.#model, {
            searchPlaceholder: "Search the configuration",
            layout: "compact",
        });
        this.element.replaceChildren(this.#form.element);
        this.#form.focusSearch();
    }

    /** Show `text` in place of the form, with a button to try again when the server could not start. */
    #message(text: string, offerRetry = false): void {
        this.#form?.dispose();
        this.#form = undefined;
        this.#model = undefined;
        const message = document.createElement("div");
        message.className = "wb-sdkconfig-message";
        const paragraph = document.createElement("p");
        paragraph.textContent = text;
        message.append(paragraph);
        if (offerRetry) {
            const button = document.createElement("button");
            button.type = "button";
            button.textContent = "Try Again";
            button.addEventListener("click", () => this.#connect());
            message.append(button);
        }
        this.element.replaceChildren(message);
    }

    async #reconnectIfChanged(): Promise<void> {
        const menus = this.#menus;
        if (menus === undefined) return;
        const stat = await this.#runtime.fs.stat(menus.path).catch(() => undefined);
        if (stat?.mtimeMs !== menus.mtime && this.#menus === menus) this.#connect();
    }

    /** The server refused part of a change: say so above the form. */
    #error(text: string): void {
        this.element.querySelector(".wb-sdkconfig-error")?.remove();
        const error = document.createElement("div");
        error.className = "wb-sdkconfig-error";
        error.setAttribute("role", "alert");
        error.textContent = text;
        this.element.prepend(error);
    }

    #close(): void {
        this.#port?.postMessage({ stop: true } satisfies ConfigServerInput);
        this.#port = undefined;
        this.#model = undefined;
        this.#menus = undefined;
    }

    async reveal(name: string): Promise<Element | string> {
        // The form comes once the server has answered; until then the message says why not.
        for (let waited = 0; this.#model === undefined; waited += 100) {
            const message = this.element.querySelector(".wb-sdkconfig-message p")?.textContent ?? LOADING;
            const pending = message === LOADING || message === CONFIGURING;
            if (!pending || waited >= (message === CONFIGURING ? 600_000 : 60_000) || this.#disposed) return message;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        const id = this.#model.itemOf(name);
        if (typeof id === "object") {
            return id.missing === "unknown"
                ? `CONFIG_${name} is not an option of this configuration.`
                : `CONFIG_${name} has no row in the editor: it has no prompt, and other options set its value.`;
        }
        if (!this.#model.state(id).visible)
            return `CONFIG_${name} is hidden now, because an option it depends on is off.`;
        this.#form!.search(`CONFIG_${name}`);
        await new Promise(requestAnimationFrame);
        return this.element.querySelector(`[data-id="${CSS.escape(id)}"]`) ?? `CONFIG_${name} was not drawn.`;
    }

    #dispose(): void {
        if (openEditors.get(this.#path) === this) openEditors.delete(this.#path);
        this.#disposed = true;
        this.#unsubscribe();
        this.#close();
        this.#form?.dispose();
    }
}
